import { createTheme } from '@mui/material/styles';

// Import fonts from Google Fonts
const fontFamilyPrimary = '"Plus Jakarta Sans", sans-serif';
const fontFamilyMono = '"JetBrains Mono", monospace';

// Würth Markenfarben
const wurthColors = {
  // Primärfarben
  red: '#C41E3A',        // Würth Rot
  black: '#000000',      // Würth Schwarz
  darkGray: '#605D5C',   // Würth Dunkelgrau
  gray: '#959595',       // Würth Grau  
  lightGray: '#DEDEDE',  // Würth Hellgrau
  white: '#FFFFFF',      // Würth Weiß
  
  // Zusätzliche Farben für UI
  redDark: '#A01729',    // Dunkleres Rot für Hover
  redLight: '#E63946',   // Helleres Rot für Akzente
};

const wurthTheme = createTheme({
  palette: {
    primary: {
      main: wurthColors.red,
      dark: wurthColors.redDark,
      light: wurthColors.redLight,
      contrastText: wurthColors.white,
    },
    secondary: {
      main: wurthColors.darkGray,
      dark: wurthColors.black,
      light: wurthColors.gray,
      contrastText: wurthColors.white,
    },
    error: {
      main: wurthColors.red,
    },
    warning: {
      main: '#FF9800',
    },
    info: {
      main: wurthColors.darkGray,
    },
    success: {
      main: '#4CAF50',
    },
    grey: {
      50: '#FAFAFA',
      100: wurthColors.lightGray,
      200: '#F5F5F5',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: wurthColors.gray,
      600: '#757575',
      700: wurthColors.darkGray,
      800: '#424242',
      900: wurthColors.black,
    },
    text: {
      primary: wurthColors.black,
      secondary: wurthColors.darkGray,
    },
    background: {
      default: wurthColors.white,
      paper: wurthColors.white,
    },
  },
  typography: {
    fontFamily: fontFamilyPrimary,
    h1: {
      fontSize: '32px',
      fontWeight: 800,
      lineHeight: 1.2,
      color: wurthColors.black,
      fontFamily: fontFamilyPrimary,
    },
    h2: {
      fontSize: '17px',
      fontWeight: 800,
      lineHeight: 1.3,
      color: wurthColors.black,
      fontFamily: fontFamilyPrimary,
    },
    h3: {
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: 1.4,
      color: wurthColors.black,
      fontFamily: fontFamilyPrimary,
    },
    h4: {
      fontSize: '15px',
      fontWeight: 600,
      lineHeight: 1.4,
      color: wurthColors.black,
      fontFamily: fontFamilyPrimary,
    },
    h5: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.5,
      color: wurthColors.black,
      fontFamily: fontFamilyPrimary,
    },
    h6: {
      fontSize: '13px',
      fontWeight: 500,
      lineHeight: 1.5,
      color: wurthColors.black,
      fontFamily: fontFamilyPrimary,
    },
    body1: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.6,
      fontFamily: fontFamilyPrimary,
    },
    body2: {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.5,
      fontFamily: fontFamilyPrimary,
    },
    subtitle1: {
      fontSize: '12px',
      fontWeight: 500,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      fontFamily: fontFamilyPrimary,
    },
    subtitle2: {
      fontSize: '11px',
      fontWeight: 500,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      fontFamily: fontFamilyPrimary,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          backgroundColor: wurthColors.red,
          color: wurthColors.white,
          '&:hover': {
            backgroundColor: wurthColors.redDark,
          },
        },
        outlined: {
          borderColor: wurthColors.red,
          color: wurthColors.red,
          '&:hover': {
            borderColor: wurthColors.redDark,
            backgroundColor: `${wurthColors.red}10`,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        colorPrimary: {
          backgroundColor: wurthColors.red,
          color: wurthColors.white,
        },
        colorSecondary: {
          backgroundColor: wurthColors.white,
          color: wurthColors.red,
          border: `1px solid ${wurthColors.red}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: wurthColors.lightGray,
        },
        barColorPrimary: {
          backgroundColor: wurthColors.red,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: `${wurthColors.red}10`,
          color: wurthColors.redDark,
          '& .MuiAlert-icon': {
            color: wurthColors.red,
          },
        },
        standardWarning: {
          backgroundColor: '#FFF3E0',
          color: '#F57C00',
        },
        standardInfo: {
          backgroundColor: `${wurthColors.darkGray}10`,
          color: wurthColors.darkGray,
          '& .MuiAlert-icon': {
            color: wurthColors.darkGray,
          },
        },
        standardSuccess: {
          backgroundColor: '#E8F5E8',
          color: '#2E7D32',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        },
        elevation2: {
          boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
        },
        elevation3: {
          boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

export default wurthTheme;